export interface ArtifactTemplate {
  id: string;
  title: string;
  subtitle: string;
  prompt: string;
  image?: string; // Optional custom image path - falls back to default illustration if not provided
}

export const artifactTemplates: ArtifactTemplate[] = [
  {
    id: 'object-map',
    title: 'Object Map',
    subtitle: 'Visualize your top objects and their components.',
    image: undefined, // TODO: Add custom image when available
    prompt: `Create an artifact of my top 100 Salesforce objects ranked by field count.

For each object, show:
- Total field count
- Breakdown of component types (custom fields, standard fields, formula fields, relationships)
- Automation count (flows, process builders, workflow rules, triggers)
- Validation rule count

Include a visual chart showing object comparison and filters to drill down by component type.

Present summary metrics at the top showing total objects analyzed, total fields across all objects, and most complex object.`,
  },
  {
    id: 'automations-map',
    title: 'Automations Map',
    subtitle: 'See all automation across your org at a glance.',
    image: undefined,
    prompt: `Analyze all automation in my Salesforce org and Create an artifact showing counts and breakdowns for:
- Flows (active vs inactive)
- Process Builders (active vs inactive)
- Workflow Rules (active vs inactive)
- Apex Triggers

Group automation by object and include a distribution chart showing automation types.

Add filters to view by automation type, status (active/inactive), and object.

Display summary metrics: total automation count, active vs inactive ratio, most automated object, and legacy automation count (Workflow Rules + Process Builders that should be migrated).`,
  },
  {
    id: 'workflow-rules-migration',
    title: 'Workflow Rules Migration Plan',
    subtitle: 'Plan your migration from Workflow Rules to Flows.',
    image: undefined,
    prompt: `Create an artifact for Workflow Rules migration plan for my org.

Identify all objects with Workflow Rules, ranked by rule count.

For each object, list all Workflow Rules with:
- Rule name
- Evaluation criteria
- Actions performed (email alerts, field updates, tasks, outbound messages)
- Complexity score (simple/medium/complex)

Group rules by migration priority (high/medium/low) based on complexity and business impact.

Include a migration roadmap with:
- Total rules to migrate
- Estimated effort per group
- Recommended migration order
- Potential consolidation opportunities

Add visual charts showing rule distribution by object and complexity breakdown.`,
  },
  {
    id: 'field-cleanup',
    title: 'Field Cleanup',
    subtitle: 'Find unused and duplicate fields to clean up.',
    image: undefined,
    prompt: `Analyze custom fields across my Salesforce org and produce an artifact for cleanup analysis.

Create two rankings:
- The top 5 objects by total custom field count
- The top 5 objects by number of custom fields with 0% utilization

For each object appearing in either ranking, include:
- The total custom field count
- The count of 0% utilization fields
- Utilization percentage (percent of records with non-null values)

Then, for each object, flag fields for cleanup consideration using these categories:
- Unused fields (0% utilization for 90+ days)
- Duplicate fields (similar names or overlapping purpose)
- Deprecated fields (marked obsolete in description/help text)
- Stale fields (not modified in 2+ years and low utilization)

Categorize flagged fields by cleanup priority (high/medium/low) based on utilization and age.

Include summary metrics across the org:
- Total fields analyzed
- Total cleanup candidates
- Potential storage savings
- The objects with the highest cleanup opportunity

Add interactive filters to explore results by object, utilization threshold, field age (creation date / last modified date), cleanup category (unused/duplicate/deprecated/stale), and ranking type (field count vs. 0%-utilization count).`,
  },
  {
    id: 'security-scan',
    title: 'Security Scan',
    subtitle: 'Audit and remediate Salesforce security risks.',
    image: undefined,
    prompt: `Scan my Salesforce org for security risks and Create an artifact for a security audit.

Analyze:
- Profiles with excessive permissions (ModifyAllData, ViewAllData, ViewEncryptedData)
- Sharing rules that may expose sensitive data
- Permission sets with admin-level access
- Objects without field-level security
- Users with admin privileges who haven't logged in recently
- Publicly accessible sites or communities

Categorize each risk by severity (critical/high/medium/low) and provide specific remediation steps for each finding.

Include summary metrics:
- Total risks found
- Risk distribution by severity
- Highest-risk profiles
- Compliance gaps

Add filters to view by risk type, severity, and affected objects.`,
  },
  {
    id: 'cpq-configuration',
    title: 'CPQ Configuration',
    subtitle: 'Audit and optimize Salesforce CPQ configuration.',
    image: undefined,
    prompt: `Analyze all CPQ configuration in my Salesforce org and create an artifact showing:

Configuration Inventory:
- Counts and breakdowns: Price Rules, Product Rules, Configuration Attributes, Quote/Contract/Subscription Line Fields (active vs inactive)
- Group by object (Product2, Pricebook2, SBQQ__Quote, SBQQ__QuoteLine, SBQQ__Contract, SBQQ__ContractLine, etc.)
- Distribution chart of CPQ component types and status

Configuration Relationships:
- Map relationships between Price Rules, Product Rules, and Configuration Attributes
- Dependencies between CPQ objects and standard/custom objects
- Fields referenced by CPQ configurations

CPQ Settings & Analysis:
- CPQ package settings and feature enablement
- CPQ-specific validation rules, automation, and approval processes

Summary Metrics:
- Total CPQ configuration count, active vs inactive ratio, most configured CPQ object
- Configuration complexity score (rules and relationships)
- Missing/incomplete configurations (e.g., products without price rules)

Filters: By component type, status (active/inactive), object, complexity

Recommendations: Redundant/conflicting rules, inactive configs to review, rule evaluation order optimizations, missing configs impacting quote accuracy`,
  },
  {
    id: 'apex-analysis',
    title: 'Apex Analysis',
    subtitle: 'Analyze, refactor, and de-risk Salesforce Apex code.',
    image: undefined,
    prompt: `Analyze all Apex code in my Salesforce org and create an artifact showing:

Apex Inventory:
- Apex Classes: total count, active vs inactive, test vs non-test classes, character count and LOC per class
- Apex Triggers: total count, active vs inactive, grouped by object, character count and LOC per trigger

Error Analysis:
- All classes/triggers with compilation errors (syntax errors, missing dependencies)
- Error details: type, message, affected component, grouped by severity (blocking vs warnings)

Code Quality Metrics:
- Average character count per class/trigger, top 10 largest by character count
- Classes/triggers exceeding Salesforce limits (1MB per class/trigger)
- Test coverage percentage per class, classes without test classes

Dependency & Usage Analysis:
- Dependencies between Apex classes, circular dependencies
- Apex classes referenced by Flows, Process Builders, or other automations
- Apex classes referencing specific Salesforce objects
- Unused Apex classes (not referenced by triggers, flows, or other classes)
- Objects with most Apex triggers, Apex classes per object

Summary Metrics:
- Total classes/triggers count, total character count, error count (by severity)
- Average test coverage percentage, most automated object (by trigger count)

Filters: By component type (Class/Trigger), status (active/inactive, with/without errors), object, character count range, test coverage percentage

Recommendations: Fix compilation errors, refactor oversized classes, consolidate redundant code, create test classes for uncovered code, flag deprecated/unused code for removal`,
  },
];
